import { memo, useCallback, useMemo } from 'react';
import { useDeleteIntegrationX } from '../../../api/delete-integration-x.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_INTEGRATION_X_QUERY_KEY } from '../../../api/get-list-integration-x.api';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

export type TDeleteIntegrationXModalType = 'DeleteIntegrationXModal';
export interface IDeleteIntegrationXModalProps {
  organizationChannelId: string;
}

const TOAST_MESSAGES_DELETE_INTEGRATION_X = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete X integration!',
};

const DeleteIntegrationXModal = memo(({ organizationChannelId }: IDeleteIntegrationXModalProps) => {
  const channelModalRootActions = useChannelModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenDeleteIntegrationXModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteIntegrationXModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationX = useDeleteIntegrationX({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_INTEGRATION_X.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_X_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteIntegrationXModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_INTEGRATION_X.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    if (organizationChannelId) {
      mutationDeleteIntegrationX.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }
  }, [organizationChannelId, mutationDeleteIntegrationX]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteIntegrationXModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteIntegrationXModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationX.isPending}
      title="Delete Integration X"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteIntegrationXModal.displayName = 'DeleteIntegrationXModal';

export default DeleteIntegrationXModal;
